const PrivacyPolicy = () => {
  return (
    <div className="min-h-screen bg-white pt-20">
      
      <main className="py-16 lg:py-24">
        <div className="max-w-[1200px] mx-auto px-4 sm:px-6 lg:px-8">
          
          {/* Page Title */}
          <h1 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-6">
            Privacy Policy – iSolve RCM
          </h1>

          {/* Subtitle */}
          <h2 className="text-2xl font-bold text-gray-800 mb-8">
            We Care About Your Privacy & Protect Your Data With Responsibility
          </h2>

          {/* Introduction */}
          <div className="space-y-4 text-gray-700 leading-relaxed mb-12">
            <p>
              At <strong>iSolve RCM</strong>, we take your privacy seriously. As a certified and responsible organization, we are committed to safeguarding 
              the personal and private data of everyone who visits or uses our website: www.isolvercm.com. This Privacy Policy outlines how 
              iSolve RCM collects, uses, shares, and protects your personal information while delivering a secure, user-friendly experience 
              across our platform.
            </p>
            <p>
              We provide complete transparency in how we collect and use your data, and we allow you to make informed choices about 
              your information. We also explain how we update and revise this Privacy Policy over time.
            </p>
          </div>

          {/* Key Terms Section */}
          <section className="mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-6">
              Key Terms Used in This Policy
            </h2>
            
            <div className="overflow-x-auto">
              <table className="w-full border-collapse border border-gray-300">
                <thead>
                  <tr className="bg-gray-100">
                    <th className="border border-gray-300 px-6 py-4 text-left font-bold text-gray-900">Term</th>
                    <th className="border border-gray-300 px-6 py-4 text-left font-bold text-gray-900">Definition</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td className="border border-gray-300 px-6 py-4 font-semibold text-gray-900">Personal Information</td>
                    <td className="border border-gray-300 px-6 py-4 text-gray-700">
                      Refers to data that can identify an individual, such as name, address, email, phone number, and Social Security 
                      number. iSolve RCM does not knowingly collect data from children under 13.
                    </td>
                  </tr>
                  <tr className="bg-gray-50">
                    <td className="border border-gray-300 px-6 py-4 font-semibold text-gray-900">Site</td>
                    <td className="border border-gray-300 px-6 py-4 text-gray-700">
                      Refers to the iSolve RCM website: www.isolvercm.com
                    </td>
                  </tr>
                  <tr>
                    <td className="border border-gray-300 px-6 py-4 font-semibold text-gray-900">Users</td>
                    <td className="border border-gray-300 px-6 py-4 text-gray-700">
                      Individuals who visit, interact with, or utilize the services of our website.
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </section>

          {/* Why We Collect Section */}
          <section className="mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-6">
              Why We Collect Your Personal Information
            </h2>
            
            <p className="text-gray-700 mb-4">We collect personal data to:</p>
            
            <ul className="list-disc list-inside space-y-2 text-gray-700 mb-6">
              <li>Enhance our products and services</li>
              <li>Deliver customized and accurate medical billing solutions.</li>
              <li>Improve user identification and secure authentication.</li>
              <li>Perform anonymous data reporting and system diagnostics.</li>
              <li>Conduct contract research when necessary.</li>
            </ul>

            <p className="text-gray-700 mb-4">
              You may receive non-promotional, service-related notifications (e.g., site maintenance alerts) from time to time. You may opt 
              out of promotional emails at any time by unsubscribing through the provided link in the email.
            </p>

            <p className="text-gray-700">
              To subscribe to our newsletter or updates, we require your name and email address. We respect your right to opt out at any time.
            </p>
          </section>

          {/* IP Address Collection */}
          <section className="mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-6">
              IP Address Collection
            </h2>
            
            <p className="text-gray-700 mb-4">
              Your IP address is automatically collected when you visit www.isolvercm.com. We may use this data to:
            </p>
            
            <ul className="list-disc list-inside space-y-2 text-gray-700">
              <li>Diagnose technical issues based on ISP or hosting environments</li>
              <li>Monitor visitor patterns from different geographic regions.</li>
            </ul>
          </section>

          {/* Accessing & Updating */}
          <section className="mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-6">
              Accessing & Updating Your Personal Information
            </h2>
            
            <p className="text-gray-700 mb-4">
              We collect your personal information when you subscribe to services, request information, or sign up for updates. Additionally, 
              we use cookies to gather data on your preferences and content interests.
            </p>

            <p className="text-gray-700 mb-4">
              If you wish to update, correct, or delete your personal information or opt out of our services, you can contact our support team. 
              We will honor your request in line with applicable laws and data retention obligations.
            </p>

            <p className="text-gray-700">
              We retain personal data as long as necessary to provide services or fulfill legal responsibilities.
            </p>
          </section>

          {/* Security */}
          <section className="mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-6">
              Security of Your Information
            </h2>
            
            <p className="text-gray-700">
              We follow industry best practices and federal standards to protect your information. While we use secure methods for data 
              transmission and storage, no system can guarantee 100% protection. We strive to mitigate risks, but encourage users to contact 
              us with any concerns at info@isolvercm.com.
            </p>
          </section>

          {/* Customer Testimonials */}
          <section className="mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-6">
              Customer Testimonials
            </h2>
            
            <p className="text-gray-700 mb-4">
              We occasionally display client testimonials or reviews on our website and marketing channels. This may include your name, 
              organization, or email, but only after receiving your explicit consent.
            </p>

            <p className="text-gray-700">
              If you want to update or remove your testimonial, please contact us at info@isolvercm.com.
            </p>
          </section>

          {/* User-Shared Information */}
          <section className="mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-6">
              User-Shared Information on Public Forums
            </h2>
            
            <p className="text-gray-700">
              Please avoid sharing personal data in public areas of our site, such as blog comments or forums, as this information can be 
              collected and used by others. iSolve RCM is not responsible for the data you voluntarily submit in these public areas.
            </p>
          </section>

          {/* Third-Party Links */}
          <section className="mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-6">
              Links to Third-Party Sites
            </h2>
            
            <p className="text-gray-700">
              Our website may include links to other websites. Once you click on these links, you leave our site. We do not control or take 
              responsibility for third-party data practices. We recommend reviewing their privacy policies before sharing any data.
            </p>
          </section>

          {/* Referral Program */}
          <section className="mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-6">
              Referral Program
            </h2>
            
            <p className="text-gray-700">
              If you refer someone to our website, we may collect the referrer's name and email address for contact purposes. If you or the 
              referred party wants this data removed, please contact us at info@isolvercm.com.
            </p>
          </section>

          {/* HIPAA Compliance */}
          <section className="mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-6">
              Use of PHI & De-Identified Data (HIPAA Compliance)
            </h2>
            
            <p className="text-gray-700 mb-4">
              iSolve RCM complies with HIPAA standards and may handle Protected Health Information (PHI) when offering billing and data 
              services. We may use PHI to provide data aggregation service or generate de-identified datasets per 45 CFR 164.514(a)-(c).
            </p>

            <p className="text-gray-700">
              We reserve the right to use such de-identified data for improving our software, services, technical support, and operational 
              processes, always in compliance with HIPAA regulations.
            </p>
          </section>

          {/* Behavioral Targeting */}
          <section className="mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-6">
              Behavioral Targeting & Retargeting
            </h2>
            
            <p className="text-gray-700 mb-4">
              We work with advertising partners who may use cookies and web beacons to track your online activity and deliver relevant ads 
              based on your preferences. This may include interest-based ads across various platforms.
            </p>

            <p className="text-gray-700">
              To learn more about how Google uses cookies, please visit Google Ads Settings.
            </p>
          </section>

          {/* Data Sharing */}
          <section className="mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-6">
              Data Sharing & Legal Disclosures
            </h2>
            
            <p className="text-gray-700 mb-4">
              We do not sell, rent, or share your personal information for promotional use without your consent. However, we may disclose 
              data when legally required, such as:
            </p>

            <ul className="list-disc list-inside space-y-2 text-gray-700 mb-4">
              <li>Responding to legal processes (e.g., subpoenas)</li>
              <li>Investigating policy violations</li>
              <li>Sharing with trusted partners to deliver requested services</li>
            </ul>

            <p className="text-gray-700">
              We may also share de-identified or aggregated data with partners to improve services and business operations.
            </p>
          </section>

          {/* Policy Updates */}
          <section className="mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-6">
              Privacy Policy Updates
            </h2>
            
            <p className="text-gray-700">
              iSolve RCM may revise this Privacy Policy at any time. We encourage you to check this page regularly for updates and changes. 
              Continued use of our website after changes indicates acceptance of the updated terms.
            </p>
          </section>

          {/* Contact Us */}
          <section className="mb-2 bg-blue-50 rounded-2xl p-8">
            <h2 className="text-3xl font-bold text-gray-900 mb-6">
              Contact Us
            </h2>
            
            <p className="text-gray-700 mb-4 font-semibold">
              Have questions, concerns, or suggestions?
            </p>

            <div className="space-y-2 text-gray-700">
              <p><strong>Phone:</strong> 848-266-5475</p>
              <p><strong>Email:</strong> info@isolvercm.com</p>
              <p><strong>Website:</strong> www.isolvercm.com</p>
            </div>
          </section>

        </div>
      </main>
    </div>
  );
};

export default PrivacyPolicy;
